/**
 * MessageLedgerTab.tsx — the "Todas as mensagens" ledger (Fowler's design
 * doc, Slice 2). Chronological, direction shown as left/right alignment plus
 * a label, `source`/`status` as colour-coded badges (colour ALWAYS paired
 * with a text label — WCAG 1.4.1, matching `ConversationBadges`'s
 * convention), `wamid` truncated with copy-on-click. `body` is unmasked —
 * rendered verbatim with `whitespace-pre-wrap`, never truncated.
 *
 * Sole reader (Aron's 2026-09-19 ruling): the Overview transcript pane and
 * the "Auditoria" tab are gone — this is the one place to read a
 * conversation. Rows render strictly oldest-first regardless of API order,
 * and the page opens scrolled to the newest message.
 *
 * The decision ledger is always overlaid inline (Aron's 2026-09-20 ruling):
 * each turn's decision rows render right after the inbound bubble that
 * opened it (`MessageRow.turn_id` ↔ `DecisionRow.turn_id`; outbound rows
 * carry no `turn_id` and are not an anchor). Grouping/rendering logic below
 * is lifted from the deleted `AuditTab.tsx`.
 */

import { useCallback, useEffect, useMemo, useRef, useState, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import type { ApiClient } from '../../api/client';
import type { DecisionRow, MessageRow } from '../../api/types';
import { Badge as PrimitiveBadge, type BadgeTone } from '../../components/Badge';
import { UnauthorizedScreen } from '../../auth/UnauthorizedScreen';
import { formatDateTime } from '../leads/formatDateTime';
import { useConversationDecisions } from './useConversationDecisions';
import { useConversationMessages } from './useConversationMessages';
import floating from '../../lib/floating';

type Tone = 'neutral' | 'info' | 'active' | 'positive' | 'terminal' | 'caution';

const TONE_MAP: Record<Tone, BadgeTone> = {
  neutral: 'neutral',
  info: 'accent',
  active: 'degraded',
  positive: 'ok',
  terminal: 'down',
  caution: 'unknown',
};

const SOURCE_TONE: Record<string, Tone> = {
  outbox_inbound: 'info',
  outbox_outbound: 'active',
  transcript: 'neutral',
};

const STATUS_TONE: Record<string, Tone> = {
  published: 'positive',
  sent: 'positive',
  delivered: 'positive',
  read: 'positive',
  pending: 'info',
  suppressed_opted_out: 'caution',
  blocked_template_window: 'caution',
  dead_letter: 'terminal',
  failed: 'terminal',
  meta_permanent_error: 'terminal',
};

const STAGE_TONE: Record<DecisionRow['stage'], Tone> = {
  webhook: 'info',
  loop: 'active',
  dispatch: 'neutral',
};

const Badge = ({ tone, label, testid }: { tone: Tone; label: string; testid: string }) => (
  <PrimitiveBadge tone={TONE_MAP[tone]} data-testid={testid}>
    {label}
  </PrimitiveBadge>
);

const SourceBadge = ({ source }: { source: string }) => {
  const { t } = useTranslation();
  const tone = SOURCE_TONE[source] ?? 'neutral';
  const label = t(`conversations.messages.source.${source}`, {
    defaultValue: t('conversations.messages.unknownValue', { value: source }),
  });
  return <Badge tone={tone} label={label} testid="msg-ledger-source-badge" />;
};

const StatusBadge = ({ status, wamid }: { status: string; wamid: string | null }) => {
  const { t } = useTranslation();
  // Fowler's note: a `status: "sent"` transcript row (no wamid — dispatch was
  // never confirmed against Meta) must NOT read as a delivery confirmation.
  // Confirmed styling is reserved for outbox_outbound rows that carry a
  // wamid; an unconfirmed "sent" degrades to a neutral "registrado" badge.
  const isUnconfirmedSent = status === 'sent' && wamid == null;
  const tone = isUnconfirmedSent ? 'neutral' : (STATUS_TONE[status] ?? 'neutral');
  const label = isUnconfirmedSent
    ? t('conversations.messages.status.recorded')
    : t(`conversations.messages.status.${status}`, {
        defaultValue: t('conversations.messages.unknownValue', { value: status }),
      });
  return <Badge tone={tone} label={label} testid="msg-ledger-status-badge" />;
};

const WamidCopy = ({ wamid }: { wamid: string | null }) => {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  if (wamid == null) {
    return <span data-testid="msg-ledger-wamid-none">{t('conversations.messages.noWamid')}</span>;
  }

  const truncated = wamid.length > 12 ? `${wamid.slice(0, 12)}…` : wamid;

  return (
    <button
      type="button"
      data-testid="msg-ledger-wamid-copy"
      title={t('conversations.messages.wamidCopy')}
      aria-label={t('conversations.messages.wamidCopy')}
      onClick={() => {
        floating(
          navigator.clipboard.writeText(wamid).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
          }),
        );
      }}
      className="inline-flex min-h-6 items-center rounded font-mono text-meta text-ink-3 underline decoration-dotted underline-offset-2 hover:text-ink-1 focus:outline-none focus:ring-2 focus:ring-outline-focus"
    >
      {copied ? t('conversations.messages.wamidCopied') : truncated}
    </button>
  );
};

const MessageRowItem = ({ row, locale }: { row: MessageRow; locale: string }) => {
  const isInbound = row.direction === 'inbound';
  const { t } = useTranslation();
  return (
    <li
      data-testid="msg-ledger-row"
      data-direction={row.direction}
      className={`flex flex-col gap-1.5 rounded-lg border px-4 py-3 ${
        isInbound
          ? 'mr-auto items-start border-bubble-in-line bg-bubble-in'
          : 'ml-auto items-end border-bubble-out-line bg-bubble-out'
      } max-w-[85%]`}
    >
      <div className="flex flex-wrap items-center gap-2">
        <span
          data-testid="msg-ledger-direction"
          className="text-meta font-semibold uppercase tracking-wide text-ink-3"
        >
          {t(`conversations.messages.direction.${row.direction}`)}
        </span>
        <SourceBadge source={row.source} />
        <StatusBadge status={row.status} wamid={row.wamid} />
      </div>
      <p data-testid="msg-ledger-body" className="whitespace-pre-wrap text-body text-ink-1">
        {row.body}
      </p>
      <div className="flex items-center gap-3 text-meta text-ink-3">
        <time dateTime={row.at} data-testid="msg-ledger-time">
          {formatDateTime(row.at, locale)}
        </time>
        <WamidCopy wamid={row.wamid} />
      </div>
    </li>
  );
};

// System lookup rows (Aron's 2026-09-28 ruling): a distinct, neutral entry —
// never counted as a client (inbound) or Mari (outbound) message, never a
// chat bubble. Direction is carried in a visible label, not colour alone
// (WCAG 1.4.1).
const SystemRowItem = ({ row, locale }: { row: MessageRow; locale: string }) => {
  const { t } = useTranslation();
  return (
    <li
      data-testid="msg-ledger-system-row"
      data-direction={row.direction}
      className="mx-auto flex max-w-[85%] items-center gap-2 rounded-full bg-surface-3 px-3 py-1 text-meta text-ink-3 ring-1 ring-inset ring-ink-3/20"
    >
      <svg
        aria-hidden="true"
        viewBox="0 0 16 16"
        className="h-3.5 w-3.5 shrink-0"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
      >
        <circle cx="8" cy="8" r="2.25" />
        <path d="M8 1.5v2M8 12.5v2M1.5 8h2M12.5 8h2M3.4 3.4l1.4 1.4M11.2 11.2l1.4 1.4M3.4 12.6l1.4-1.4M11.2 4.8l1.4-1.4" />
      </svg>
      <span data-testid="msg-ledger-system-label" className="font-semibold uppercase tracking-wide">
        {t('conversations.messages.direction.system')}
      </span>
      <span data-testid="msg-ledger-system-body">{row.body}</span>
      <time dateTime={row.at}>{formatDateTime(row.at, locale)}</time>
    </li>
  );
};

// ---------------------------------------------------------------------------
// Decision-ledger overlay (always shown). Lifted from
// the deleted AuditTab.tsx: grouping by turn_id, the per-row technical
// rendering, and the collapsed raw-payload table.
// ---------------------------------------------------------------------------

const groupDecisionsByTurn = (decisions: DecisionRow[]): Map<string, DecisionRow[]> => {
  const sorted = [...decisions].sort((a, b) => a.at.localeCompare(b.at) || a.seq - b.seq);
  const byTurn = new Map<string, DecisionRow[]>();
  sorted.forEach((decision) => {
    const group = byTurn.get(decision.turn_id);
    if (group) {
      group.push(decision);
    } else {
      byTurn.set(decision.turn_id, [decision]);
    }
  });
  return byTurn;
};

const DecisionPayloadTable = ({ payload }: { payload: Record<string, unknown> }) => {
  const { t } = useTranslation();
  const entries = Object.entries(payload);
  if (entries.length === 0) return null;
  return (
    <table data-testid="msg-ledger-decision-payload-table" className="w-full text-meta text-ink-2">
      <caption className="sr-only">{t('conversations.messages.decision.payloadCaption')}</caption>
      <tbody>
        {entries.map(([key, value]) => (
          <tr key={key} className="align-top">
            <th
              scope="row"
              className="whitespace-nowrap pr-3 py-0.5 text-left font-medium text-ink-3"
            >
              {key}
            </th>
            <td className="whitespace-pre-wrap py-0.5">
              {typeof value === 'string' ? value : JSON.stringify(value)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

// One technical decision row (stage, event, step_id, verdict, reason, payload).
const DecisionRowItem = ({ decision, locale }: { decision: DecisionRow; locale: string }) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const hasPayload = Object.keys(decision.payload).length > 0;

  return (
    <li
      data-testid="msg-ledger-decision-row"
      className="flex flex-col gap-1 border-l-2 border-ink-3/20 py-2 pl-4"
    >
      <div className="flex flex-wrap items-center gap-2">
        <Badge
          tone={STAGE_TONE[decision.stage]}
          label={t(`conversations.messages.decision.stage.${decision.stage}`)}
          testid="msg-ledger-decision-stage-badge"
        />
        <span data-testid="msg-ledger-decision-event" className="text-body font-medium text-ink-1">
          {decision.event}
        </span>
        {decision.verdict != null ? (
          <span data-testid="msg-ledger-decision-verdict" className="text-meta text-ink-2">
            {t('conversations.messages.decision.verdict', { value: decision.verdict })}
          </span>
        ) : null}
      </div>
      <div className="flex flex-wrap items-center gap-3 text-meta text-ink-3">
        <time dateTime={decision.at}>{formatDateTime(decision.at, locale)}</time>
        {decision.step_id != null ? (
          <span>{t('conversations.messages.decision.stepId', { value: decision.step_id })}</span>
        ) : null}
      </div>
      {decision.reason != null ? (
        <p data-testid="msg-ledger-decision-reason" className="text-meta text-ink-2">
          {decision.reason}
        </p>
      ) : null}
      {hasPayload ? (
        <button
          type="button"
          data-testid="msg-ledger-decision-payload-toggle"
          aria-expanded={expanded}
          onClick={() => setExpanded((prev) => !prev)}
          className="inline-flex min-h-6 w-fit items-center text-meta text-ink-3 underline decoration-dotted underline-offset-2 hover:text-ink-1 focus:outline-none focus:ring-2 focus:ring-outline-focus"
        >
          {t('conversations.messages.decision.payloadToggle')}
        </button>
      ) : null}
      {expanded ? <DecisionPayloadTable payload={decision.payload} /> : null}
    </li>
  );
};

// A turn's decisions, anchored right after the inbound bubble that opened it.
// The summary line is the turn's terminal (last) decision's event/verdict/
// reason; the expand control reveals every technical row (redeliveries
// included, never deduped).
const TurnDecisions = ({ decisions, locale }: { decisions: DecisionRow[]; locale: string }) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const summary = decisions[decisions.length - 1]!;

  return (
    <li
      data-testid="msg-ledger-turn-decisions"
      className="mr-auto flex max-w-[85%] flex-col gap-1.5 rounded-lg border border-dashed border-ink-3/30 bg-surface-2 px-3 py-2"
    >
      <div
        data-testid="msg-ledger-decision-summary"
        className="flex flex-wrap items-center gap-2 text-meta text-ink-2"
      >
        <span data-testid="msg-ledger-decision-summary-event" className="font-medium text-ink-1">
          {summary.event}
        </span>
        {summary.verdict != null ? (
          <span data-testid="msg-ledger-decision-summary-verdict">
            {t('conversations.messages.decision.verdict', { value: summary.verdict })}
          </span>
        ) : null}
        {summary.reason != null ? (
          <span data-testid="msg-ledger-decision-summary-reason">{summary.reason}</span>
        ) : null}
      </div>
      <button
        type="button"
        data-testid="msg-ledger-decision-expand"
        aria-expanded={expanded}
        onClick={() => setExpanded((prev) => !prev)}
        className="inline-flex min-h-6 w-fit items-center text-meta text-ink-3 underline decoration-dotted underline-offset-2 hover:text-ink-1 focus:outline-none focus:ring-2 focus:ring-outline-focus"
      >
        {t('conversations.messages.decision.expand')}
      </button>
      {expanded ? (
        <ol data-testid="msg-ledger-decision-rows" className="flex flex-col gap-1">
          {decisions.map((decision) => (
            <DecisionRowItem
              key={`${decision.turn_id}-${decision.seq}`}
              decision={decision}
              locale={locale}
            />
          ))}
        </ol>
      ) : null}
    </li>
  );
};

export interface MessageLedgerTabProps {
  apiClient: ApiClient;
  threadId: string;
}

export const MessageLedgerTab = ({ apiClient, threadId }: MessageLedgerTabProps) => {
  const { t, i18n } = useTranslation();
  const { messages, phase, refetch } = useConversationMessages(apiClient, threadId);
  const { decisions } = useConversationDecisions(apiClient, threadId);
  const listRef = useRef<HTMLOListElement>(null);

  const onRetry = useCallback(() => refetch(), [refetch]);

  // Strictly oldest-first, regardless of API order.
  const orderedMessages = useMemo(
    () => [...messages].sort((a, b) => a.at.localeCompare(b.at)),
    [messages],
  );

  const decisionsByTurn = useMemo(() => groupDecisionsByTurn(decisions), [decisions]);

  // Open scrolled to the newest message (bottom of the chat).
  useEffect(() => {
    if (orderedMessages.length > 0) {
      listRef.current?.lastElementChild?.scrollIntoView?.();
    }
  }, [orderedMessages.length]);

  if (phase === 'unauthorized') {
    return <UnauthorizedScreen />;
  }

  if (phase === 'loading' && messages.length === 0) {
    return (
      <p role="status" data-testid="msg-ledger-loading" className="text-body text-ink-2">
        {t('conversations.messages.loading')}
      </p>
    );
  }

  if (phase === 'error') {
    return (
      <div className="flex flex-col items-start gap-3" data-testid="msg-ledger-error">
        <p role="alert" className="text-body text-status-down">
          {t('conversations.messages.errorBody')}
        </p>
        <button
          type="button"
          data-testid="msg-ledger-retry"
          onClick={onRetry}
          className="inline-flex items-center rounded-lg bg-surface-2 px-4 py-1.5 text-body font-medium text-ink-1 ring-1 ring-inset ring-ink-3/20 hover:bg-surface-1"
        >
          {t('conversations.messages.retry')}
        </button>
      </div>
    );
  }

  if (messages.length === 0) {
    return (
      <p data-testid="msg-ledger-empty" className="text-body text-ink-3">
        {t('conversations.messages.empty')}
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <ol ref={listRef} data-testid="msg-ledger-list" className="flex flex-col gap-3">
        {orderedMessages.map((row) => {
          if (row.direction === 'system') {
            return (
              <SystemRowItem
                key={`${row.direction}-${row.source}-${row.at}-${row.wamid ?? ''}`}
                row={row}
                locale={i18n.language}
              />
            );
          }
          const turnDecisions =
            row.direction === 'inbound' && row.turn_id != null
              ? decisionsByTurn.get(row.turn_id)
              : undefined;
          return (
            <Fragment key={`${row.direction}-${row.source}-${row.at}-${row.wamid ?? ''}`}>
              <MessageRowItem row={row} locale={i18n.language} />
              {turnDecisions != null ? (
                <TurnDecisions decisions={turnDecisions} locale={i18n.language} />
              ) : null}
            </Fragment>
          );
        })}
      </ol>
    </div>
  );
};
